import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { UserCog, Plus } from 'lucide-react';
import { Panel, Field, FormRow, fmtDate } from './design';

// Self-management: a participant can manage parts of their care themselves (choose their workers,
// pay invoices and be reimbursed). The provider still does the monthly direct care management and
// stays responsible, so every worker the participant engages is recorded with current screening.
// Invoices they pay themselves come in as supplier invoices paid as a participant reimbursement.

const SCREENING = [['aged_care_worker_screening', 'Aged care worker screening'], ['ndis_worker_screening', 'NDIS Worker Screening'], ['police_check', 'Police check']];

export default function SelfManagement({ plan, onSaved }) {
  const [on, setOn] = useState(!!plan.self_managed);
  const [what, setWhat] = useState((plan.self_managed_services || []).join(', '));
  const [workers, setWorkers] = useState([]);
  const [form, setForm] = useState(null);
  const [bank, setBank] = useState(null);
  const load = useCallback(() => {
    if (plan.client_id) api.get('/aged-care/sm-workers', { params: { client_id: plan.client_id } }).then((r) => setWorkers(r.data || [])).catch(() => setWorkers([]));
  }, [plan.client_id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setOn(!!plan.self_managed); setWhat((plan.self_managed_services || []).join(', ')); }, [plan.self_managed, plan.self_managed_services]);
  if (!plan.id) return null;
  const saveFlags = async (next, list) => {
    try {
      await api.patch(`/aged-care/funding/${plan.id}`, { selfManaged: next, selfManagedServices: String(list || '').split(',').map((x) => x.trim()).filter(Boolean) });
      toast.success('Saved'); if (onSaved) onSaved();
    } catch { toast.error('Could not save'); }
  };
  const addWorker = async () => {
    try {
      if (form.id) await api.put(`/aged-care/sm-workers/${form.id}`, { name: form.name, services: form.services, screeningType: form.screeningType, screeningRef: form.screeningRef, screeningExpires: form.screeningExpires || null });
      else await api.post('/aged-care/sm-workers', { clientId: plan.client_id, ...form });
      toast.success(form.id ? 'Worker saved' : 'Worker recorded'); setForm(null); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save'); }
  };
  const stopWorker = async (w) => {
    try { await api.put(`/aged-care/sm-workers/${w.id}`, { active: false }); toast.success(`${w.name} no longer recorded as working for them`); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not update'); }
  };
  const saveBank = async () => {
    const body = { reimburseBsb: bank.bsb, reimburseAccountName: bank.name, reimburseEmail: bank.email };
    if (bank.account) body.reimburseAccount = bank.account;   // left blank: the account on record stays
    try { await api.patch(`/aged-care/funding/${plan.id}`, body); toast.success('Bank account saved'); setBank(null); if (onSaved) onSaved(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not save'); }
  };
  const today = new Date().toISOString().slice(0, 10);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  return (
    <Panel title="Self-management" icon={UserCog} bodyClass="px-4 py-3 space-y-2 text-[13px]">
      <label className="flex items-center gap-2 text-slate-700 cursor-pointer">
        <input type="checkbox" checked={on} onChange={(e) => { setOn(e.target.checked); saveFlags(e.target.checked, what); }} /> They self-manage part of their care
      </label>
      {on && (
        <>
          <Field label="What they manage themselves" hint="For example: choosing their cleaner, paying the gardener and being reimbursed. Separate with commas.">
            {(id) => <input id={id} className="input-field" value={what} onChange={(e) => setWhat(e.target.value)} onBlur={() => saveFlags(on, what)} />}
          </Field>
          <p className="text-slate-500">You still provide direct care management every month and stay responsible. Pay their invoices as a participant reimbursement in Supplier invoices: the payment goes into their own account below.</p>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-slate-700">
              <span className="font-medium text-slate-800">Their account for reimbursements: </span>
              {plan.reimburse_account_set ? `${plan.reimburse_account_name || 'Account'}, BSB ${plan.reimburse_bsb || '—'}, account ${plan.reimburse_account}${plan.reimburse_email ? `, remittance to ${plan.reimburse_email}` : ''}` : 'not recorded'}
            </p>
            {!bank && <button type="button" className="btn-secondary btn-sm" onClick={() => setBank({ bsb: plan.reimburse_bsb || '', account: '', name: plan.reimburse_account_name || '', email: plan.reimburse_email || '' })}>{plan.reimburse_account_set ? 'Change' : 'Add account'}</button>}
          </div>
          {bank && (
            <div className="rounded-lg border border-slate-200 p-3 space-y-2">
              <FormRow cols={2}>
                <Field label="Account name">{(id) => <input id={id} className="input-field" maxLength={32} value={bank.name} onChange={(e) => { const v = e.target.value; setBank((b) => ({ ...b, name: v })); }} />}</Field>
                <Field label="Remittance email" hint="Optional: where the payment advice goes.">{(id) => <input id={id} type="email" className="input-field" value={bank.email} onChange={(e) => { const v = e.target.value; setBank((b) => ({ ...b, email: v })); }} />}</Field>
              </FormRow>
              <FormRow cols={2}>
                <Field label="BSB">{(id) => <input id={id} className="input-field" placeholder="062-000" value={bank.bsb} onChange={(e) => { const v = e.target.value; setBank((b) => ({ ...b, bsb: v })); }} />}</Field>
                <Field label="Account number" hint={plan.reimburse_account_set ? `Leave blank to keep ${plan.reimburse_account}.` : undefined}>{(id) => <input id={id} inputMode="numeric" className="input-field" value={bank.account} onChange={(e) => { const v = e.target.value; setBank((b) => ({ ...b, account: v })); }} />}</Field>
              </FormRow>
              <div className="flex justify-end gap-2">
                <button type="button" className="btn-ghost btn-sm" onClick={() => setBank(null)}>Cancel</button>
                <button type="button" className="btn-primary btn-sm" onClick={saveBank}>Save account</button>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between">
            <p className="font-medium text-slate-800">Workers they engage</p>
            {!form && <button type="button" className="btn-secondary btn-sm" onClick={() => setForm({ name: '', services: '', screeningType: SCREENING[0][0], screeningRef: '', screeningExpires: '' })}><Plus size={13} /> Add worker</button>}
          </div>
          {workers.filter((w) => w.active !== false).map((w) => {
            const exp = w.screening_expires ? String(w.screening_expires).slice(0, 10) : null;
            return (
              <div key={w.id} className="flex flex-wrap items-baseline justify-between gap-2">
                <p className={exp && exp < today ? 'text-red-800' : 'text-slate-700'}>
                  {w.name}{w.services ? `, ${w.services}` : ''}. {(SCREENING.find(([k]) => k === w.screening_type) || [, 'Screening'])[1]} {w.screening_ref}, {exp ? `${exp < today ? 'expired' : 'expires'} ${fmtDate(exp)}` : 'no expiry recorded'}
                </p>
                {!form && (
                  <span className="flex gap-1">
                    <button type="button" className="btn-ghost btn-sm" onClick={() => setForm({ id: w.id, name: w.name || '', services: w.services || '', screeningType: w.screening_type || SCREENING[0][0], screeningRef: w.screening_ref || '', screeningExpires: exp || '' })}>{exp && exp < today ? 'Renew' : 'Edit'}</button>
                    <button type="button" className="btn-ghost btn-sm" onClick={() => stopWorker(w)}>Stopped working for them</button>
                  </span>
                )}
              </div>
            );
          })}
          {form && (
            <div className="rounded-lg border border-slate-200 p-3 space-y-2">
              <FormRow cols={2}>
                <Field label="Name">{(id) => <input id={id} className="input-field" value={form.name} onChange={set('name')} />}</Field>
                <Field label="What they do">{(id) => <input id={id} className="input-field" value={form.services} onChange={set('services')} />}</Field>
              </FormRow>
              <FormRow cols={3}>
                <Field label="Screening">{(id) => <select id={id} className="input-field" value={form.screeningType} onChange={set('screeningType')}>{SCREENING.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>}</Field>
                <Field label="Number">{(id) => <input id={id} className="input-field" value={form.screeningRef} onChange={set('screeningRef')} />}</Field>
                <Field label="Expires">{(id) => <input id={id} type="date" className="input-field" value={form.screeningExpires} onChange={set('screeningExpires')} />}</Field>
              </FormRow>
              <div className="flex justify-end gap-2">
                <button type="button" className="btn-ghost btn-sm" onClick={() => setForm(null)}>Cancel</button>
                <button type="button" className="btn-primary btn-sm" onClick={addWorker}>{form.id ? 'Save changes' : 'Save worker'}</button>
              </div>
            </div>
          )}
        </>
      )}
    </Panel>
  );
}
